import PropTypes from "prop-types"
import React, { useCallback } from "react"
import TextChartODD13EmissionsEmployeeMolecule from "views/molecules/canvases/charts/TextChartODD13GHGEmissionsEmployeeMolecule"
import TextChartODD13GHGEmissionsMolecule from "views/molecules/canvases/charts/TextChartODD13GHGEmissionsMolecule"
import TextChartODD13EnergyMixMolecule from "views/molecules/canvases/charts/TextChartODD13EnergyMixMolecule"
import ChartODD13EquipmentsGHGEmissionsMolecule from "views/molecules/canvases/charts/ChartODD13EquipmentsGHGEmissionsMolecule"
import ScoreLayoutOrganism from "views/organisms/scores/ScoreLayoutOrganism"
import ChartODD13GHGEmissionsDistributionMolecule from "views/molecules/canvases/charts/ChartODD13GHGEmissionsDistributionMolecule"
import ChartODD13GHGEmissionsDistributionConsolidateMolecule from "views/molecules/canvases/charts/ChartODD13GHGEmissionsDistributionConsolidateMolecule"
import ChartODD13DatacenterInternalEmissionsBreakdownMolecule from "views/molecules/canvases/charts/ChartODD13DatacenterInternalEmissionsBreakdownMolecule"
import Chart3Sdg13Country from "views/molecules/canvases/Chart3SDG13Country"
import ChartODD13GHGEmissionOverviewMolecule from "views/molecules/canvases/charts/ChartODD13GHGEmissionOverviewMolecule"
import ChartODD13EmissionBreakdownPerRegionMolecule from "views/molecules/canvases/charts/ChartODD13EmissionBreakdownPerRegionMolecule"
import { useTranslation } from "react-i18next"
import { EnergyMixKpi } from "views/atoms/charts/kpi/energyMix/EnergyMixKpi"
import ChartODD13GhgPerVisitOrFallbackMolecule from "views/molecules/canvases/charts/ChartODD13GhgPerVisitOrFallbackMolecule"
import { useAppContext } from "components/AppContext"

const CountryOrSiteScoreLayoutOrganism = ({ score, isConsolidated, trendBadges }) => {
  const { t } = useTranslation()
  const { currentRootNode } = useAppContext()

  const chart1TrendBadge = trendBadges?.odd13_CO2
  const chart5TrendBadge = trendBadges?.odd13_CO2_average_employee
  const maturityTrendBadge = trendBadges?.odd13_maturity
  const chart2TrendBadge = trendBadges?.odd13_energy_mix
  
  const chart4 = useCallback(() => {
    if (isConsolidated) {
      return (
        <ChartODD13GHGEmissionsDistributionConsolidateMolecule score={score} />
      )
    }

    if (score?.has_only_internal_datacenter_impacts) {
      return (
        <ChartODD13DatacenterInternalEmissionsBreakdownMolecule
          breakdown={score?.odd13_datacenter_internal_emissions_breakdown}
        />
      )
    }

    return <ChartODD13GHGEmissionsDistributionMolecule score={score} />
  }, [score, isConsolidated])

  const chart2EnergyMix = (
    <TextChartODD13EnergyMixMolecule
      energyMixStatus={score?.odd13_energy_mix_status}
      energyMixValue={
        currentRootNode?.methodology === "bilan_carbone"
          ? Number(score?.odd13_energy_mix_value)
          : Number(score?.odd7_market_based_emission_factor)
      }
      country={EnergyMixKpi(score)}
      trendBadge={chart2TrendBadge}
    />
  )

  return (
    <ScoreLayoutOrganism
      maturity={score.odd13_maturity ?? 0}
      maturityTrendBadge={maturityTrendBadge} 
      chart1={
        <TextChartODD13GHGEmissionsMolecule
          score={score}
          ghgEmissions={score?.odd13_CO2}
          trendBadge={chart1TrendBadge}
        />
      }
      chart2={
        isConsolidated ? (
          <ChartODD13GhgPerVisitOrFallbackMolecule
            score={score}
            fallback={chart2EnergyMix}
          />
        ) : (
          chart2EnergyMix
        )
      }
      chart3={
        isConsolidated ? (
          <ChartODD13EmissionBreakdownPerRegionMolecule score={score} />
        ) : (
          <ChartODD13EquipmentsGHGEmissionsMolecule score={score} />
        )
      }
      chart4={chart4()}
      chart5={
        <TextChartODD13EmissionsEmployeeMolecule
          score={score} 
          trendBadge={chart5TrendBadge}
        />
      }
      chart6={
        isConsolidated ? (
          <ChartODD13GHGEmissionOverviewMolecule score={score} />
        ) : (
          <Chart3Sdg13Country score={score} />
        )
      }
    />
  )
}

CountryOrSiteScoreLayoutOrganism.propTypes = {
  score: PropTypes.object.isRequired,
  isConsolidated: PropTypes.bool,
  trendBadges: PropTypes.object, 
}

CountryOrSiteScoreLayoutOrganism.defaultProps = {
  isConsolidated: false,
  trendBadges: {},
}

export default CountryOrSiteScoreLayoutOrganism
